const API_KEY = 'treas-api-key-2025';
const AI_PORTAL_BASE_URL = 'https://tophamtechbeb.com/api/ai-portal/v1';
const GLOBAL_CONFIG_URL = 'https://tophamtechbeb.com/api/toptech/onboarding/v1/global-config';

const WORKFLOW_LABELS = {
  'client-lead': 'Client Lead Finder',
  'aw-biz-lead': 'Business Lead Finder',
  'aw-x-check': 'X Check',
  'aw-x-post': 'X Post',
  'aw-seo-track': 'SEO Track',
  'aw-blog': 'AI Blog Post',
  'cma': 'Comparative Market Analysis',
  'aw-backlink': 'AW Backlink'
};

const WORKFLOW_REQUESTS = {
  'client-lead': { request_type: 'lead', requester: 'client' },
  'aw-biz-lead': { request_type: 'lead', requester: 'business' },
  'aw-x-check': { request_type: 'x-check', requester: 'business' },
  'aw-x-post': { request_type: 'x-post', requester: 'business' },
  'aw-seo-track': { request_type: 'seo-track', requester: 'business' },
  'aw-blog': { request_type: 'blog', requester: 'business' },
  'cma': { request_type: 'cma', requester: 'client' },
  'aw-backlink': { request_type: 'backlink', requester: 'business' }
};

const headers = {
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY
};

const readJsonResponse = async (response) => {
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const message = data?.message || data?.error || `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return data;
};

const unwrapAIPortalRequest = (payload) => (
  payload?.data?.request ||
  payload?.data ||
  payload?.request ||
  payload
);

const normalizeAIPortalRequest = (request) => {
  if (!request) return request;
  return {
    ...request,
    id: request.id || request.request_id
  };
};

export const getWorkflowLabel = (workflowId) => WORKFLOW_LABELS[workflowId] || workflowId;

export const getGlobalConfig = async () => {
  const response = await fetch(GLOBAL_CONFIG_URL, {
    method: 'GET',
    headers: { 'X-API-Key': API_KEY }
  });
  return readJsonResponse(response);
};

const getGlobalConfigValue = (config, key) => {
  const payload = config?.data;

  // The global-config API may return the requested record as a keyed
  // { key, value } envelope alongside an older aggregate snapshot. Prefer the
  // explicit keyed value so recent enrollment changes are not hidden by the
  // snapshot, while retaining support for both aggregate response shapes.
  if (payload?.key === key && payload.value !== undefined) return payload.value;
  if (config?.key === key && config.value !== undefined) return config.value;

  return payload?.[key] ?? payload?.data?.[key] ?? config?.[key];
};

export const getAgentPortalConfig = async (siteId) => {
  const config = await getGlobalConfig();
  const portalConfig = getGlobalConfigValue(config, 'ai-portal-enrolled-agents');
  const catalog = getGlobalConfigValue(config, 'ai-portal-workflows')?.workflows || [];
  const responseShapes = getGlobalConfigValue(config, 'ai-portal-response-shapes') || {};
  const agents = portalConfig?.agents || [];
  const agent = agents.find((item) => item.site_id?.toLowerCase() === siteId.toLowerCase());

  if (!agent) {
    return {
      agent: null,
      workflows: [],
      availableWorkflows: portalConfig?.available_workflows || [],
      responseShapes
    };
  }

  const workflows = (agent.enrolled_workflows || [])
    .map((workflowId) => {
      const requestConfig = WORKFLOW_REQUESTS[workflowId];
      if (!requestConfig) return null;

      // Prefer the stable workflow ID. Some catalog entries use a worker-facing
      // request_type that differs from the public requests API value (CMA is
      // cataloged as "comps" but submitted to /requests as "cma").
      const catalogEntry = catalog.find((item) => item.workflow === workflowId) ||
        catalog.find((item) => (
          item.request_type === requestConfig.request_type && item.requester === requestConfig.requester
        ));

      return {
        id: workflowId,
        label: getWorkflowLabel(workflowId),
        description: catalogEntry?.description || '',
        ...requestConfig
      };
    })
    .filter(Boolean);

  return {
    agent,
    workflows,
    availableWorkflows: portalConfig?.available_workflows || [],
    responseShapes
  };
};

export const createAIPortalRequest = async ({
  siteid,
  requester,
  request_type,
  request_detail
}) => {
  const response = await fetch(`${AI_PORTAL_BASE_URL}/requests`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      siteid,
      requester,
      request_type,
      request_detail: request_detail || ''
    })
  });

  const payload = await readJsonResponse(response);
  const request = normalizeAIPortalRequest(unwrapAIPortalRequest(payload));

  if (!request?.id) {
    throw new Error('AI Portal create response did not include a request id.');
  }

  return request;
};

export const getAIPortalRequest = async (id, siteId) => {
  if (!id) {
    throw new Error('Cannot check AI Portal status without a request id.');
  }

  const requests = await getAIPortalRequests(siteId);
  const request = requests.find((item) => String(item.id || item.request_id) === String(id));

  if (!request) {
    throw new Error(`AI Portal request ${id} was not found.`);
  }

  return normalizeAIPortalRequest(request);
};

export const getAIPortalRequests = async (siteId) => {
  const params = siteId ? `?siteid=${encodeURIComponent(siteId)}` : '';
  const response = await fetch(`${AI_PORTAL_BASE_URL}/requests${params}`, {
    method: 'GET',
    headers: { 'X-API-Key': API_KEY }
  });

  const payload = await readJsonResponse(response);
  return (Array.isArray(payload?.data) ? payload.data : [])
    .map(normalizeAIPortalRequest);
};
